/**
 * Browser geolocation, normalised.
 *
 * The first browser permission this app has ever asked for, so there is no
 * house pattern to follow and this file sets one. Two things it is careful
 * about, both of which are the difference between working and appearing to
 * work on a real handset:
 *
 * 1. **Never prompt without a gesture.** A getCurrentPosition() fired from
 *    componentDidMount is a prompt the player did not ask for, arriving before
 *    they have read why. Safari on iOS is markedly less reliable about showing
 *    it, and Chrome quietly suppresses repeats it decides were unsolicited —
 *    and a suppressed prompt is indistinguishable from a denied one, so the
 *    player is told to allow something they were never asked. `permissionState`
 *    exists so a caller can find out whether a read will be silent, and only
 *    then take it without a tap.
 *
 * 2. **A missing API is not a denial.** navigator.geolocation is undefined
 *    outside a secure context. https:// and http://localhost are secure;
 *    http://192.168.x.x is not, which is exactly how somebody testing from
 *    their phone on the office wifi will load this. "Unsupported" says so
 *    instead of telling them to accept a prompt that can never appear.
 */

export const GRANTED = "granted";
export const DENIED = "denied";
export const UNAVAILABLE = "unavailable";
export const UNSUPPORTED = "unsupported";
/**
 * Refused, but not by the player.
 *
 * PERMISSION_DENIED does not say who denied it. On Windows a site the player
 * has explicitly allowed still returns code 1 when the operating system's own
 * location switch is off -- so the page told somebody looking at a green
 * "Location: allowed" toggle that their browser was remembering a no they
 * never said. Blaming the player for a setting two levels above the page is
 * worse than saying nothing: they go and re-allow the thing that was already
 * allowed, it fails again, and there is nowhere left to look.
 */
export const SYSTEM_DENIED = "system_denied";

/** How long to wait for a fix. Indoors on wifi positioning this is not fast. */
const TIMEOUT_MS = 15000;

/** Accept a fix the browser already has, if it is under a minute old. */
const MAX_AGE_MS = 60000;

export function isSupported(){
    return typeof navigator !== "undefined" && !!navigator.geolocation;
}

/**
 * "granted" / "denied" / "prompt" / null when the browser will not say.
 *
 * Permissions.query is unavailable on older Safari, where the honest answer is
 * "no idea" — null, so the caller falls back to requiring a tap rather than
 * assuming either way.
 */
export function permissionState(){
    if(!isSupported()) return Promise.resolve(DENIED);
    if(typeof navigator.permissions === "undefined" || !navigator.permissions.query){
        return Promise.resolve(null);
    }
    try {
        return navigator.permissions.query({ name: "geolocation" })
            .then(status => status.state)
            .catch(() => null);
    } catch (e) {
        // Firefox has historically thrown on an unrecognised permission name.
        return Promise.resolve(null);
    }
}

/**
 * Read the current position.
 *
 * Always resolves, never rejects: every outcome here is an ordinary thing a
 * player can do, and a caller that has to both .then and .catch to handle
 * "they said no" will eventually forget one.
 *
 *   { status: "granted", latitude, longitude, accuracy }
 *   { status: "denied" | "unavailable" | "unsupported" }
 */
export function readPosition(){
    if(!isSupported()){
        return Promise.resolve({ status: UNSUPPORTED });
    }

    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                var coords = position.coords || {};
                resolve({
                    status: GRANTED,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    accuracy: coords.accuracy,
                });
            },
            (error) => {
                // PERMISSION_DENIED is 1. A timeout or a device that cannot get
                // a fix is not a refusal and must not be answered as one — the
                // player did nothing wrong and retrying may well work.
                if(!(error && error.code === 1)){
                    resolve({ status: UNAVAILABLE });
                    return;
                }
                // Denied — but by whom? Ask the permission the player controls.
                // Still "granted" means the block is above this page, in the
                // device's own settings, and the player has nothing to undo.
                permissionState().then((state) => {
                    resolve({
                        status: state === GRANTED ? SYSTEM_DENIED : DENIED,
                    });
                });
            },
            {
                enableHighAccuracy: true,
                timeout: TIMEOUT_MS,
                maximumAge: MAX_AGE_MS,
            }
        );
    });
}
